import { useState, useMemo, useEffect } from "react";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import moment from "moment-timezone";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  getInvoiceById,
  getInvoiceGroup,
  setInvoiceEnteredAcct,
  sortInvoiceItems,
  formatCurrency,
  formatInvoiceNo,
  statusStyle,
} from "./Api";
import { printInvoice, downloadInvoicePdf, prefetchPdfLibs } from "./InvoicePrintView";

const StatusPill = ({ status, large }) => {
  const cfg = statusStyle(status);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      borderRadius: 20,
      padding: large ? "6px 16px" : "3px 12px",
      fontSize: large ? "0.9rem" : "0.74rem",
      fontWeight: 700,
    }}>
      <span style={{ width: large ? 9 : 6, height: large ? 9 : 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
      {status}
    </span>
  );
};

const Section = ({ icon, title, badge, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="iv-drawer-section">
      <button type="button" className="iv-drawer-section__header" onClick={() => setOpen((p) => !p)}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FeatherIcon icon={icon} size={14} color="#4F46E5" />
          <span>{title}</span>
          {badge !== undefined && (
            <span className="iv-drawer-section__badge">{badge}</span>
          )}
        </div>
        <FeatherIcon icon={open ? "chevron-up" : "chevron-down"} size={14} color="#94A3B8" />
      </button>
      {open && <div className="iv-drawer-section__body">{children}</div>}
    </div>
  );
};

const linePayable = (item) =>
  Number(item?.total != null ? item.total : item?.amount) || 0;


const InvoiceDetailDrawer = ({
  isOpen,
  onClose,
  invoice,
  canEdit,
  canApprove = false,
  canMarkPaid = false,
  canSetEntered = false,
  busy = false,
  onApprove,
  onDecline,
  onMarkPaid,
  onChange,
  onForward,
  onEdit,
  onPreview,
}) => {
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    setDownloading(true);
    try { await downloadInvoicePdf(invoice); } finally { setDownloading(false); }
  };

  // Warm the pdf libs while the user is reading, so Download is instant.
  useEffect(() => { if (isOpen) prefetchPdfLibs(); }, [isOpen]);
  const [refreshing, setRefreshing] = useState(false);
  const [enteringAcct, setEnteringAcct] = useState(false);

  const refresh = async () => {
    if (!invoice?._id) return;
    setRefreshing(true);
    try {
      const fresh = await getInvoiceById(invoice._id);
      if (fresh) onChange?.(fresh);
    } catch {
      // toast handled
    } finally {
      setRefreshing(false);
    }
  };

  const toggleEnteredAcct = async () => {
    if (!invoice?._id) return;
    const entered = !invoice.enteredAcctAt;
    if (!entered) {
      const res = await Swal.fire({
        title: "Clear entered-in-accounting?",
        text: "This removes the entered-in-accounting flag for this invoice.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Clear",
        confirmButtonColor: "#DC2626",
        cancelButtonText: "Cancel",
        width: 440,
      });
      if (!res.isConfirmed) return;
    }
    setEnteringAcct(true);
    try {
      const result = await setInvoiceEnteredAcct(invoice._id, entered);
      if (result?.data) onChange?.(result.data);
      toast.success(entered ? "Marked entered in accounting" : "Cleared entered in accounting");
    } catch {
      // toast handled
    } finally {
      setEnteringAcct(false);
    }
  };

  // A submission is N documents sharing one invoiceNo and one groupId. The
  // drawer is opened on whichever line the user clicked, so it pulls the rest
  // and presents the whole invoice. The endpoint applies the caller's own role
  // filter, which is why a manager gets only their properties' lines while
  // `itemCount` still reports how many really exist. Re-read on `updatedAt`
  // because approve/decline/pay now transition every line of the batch.
  const [group, setGroup] = useState(null);

  useEffect(() => {
    if (!isOpen || !invoice?.groupId) {
      setGroup(null);
      return undefined;
    }
    let cancelled = false;
    getInvoiceGroup(invoice.groupId)
      .then((g) => { if (!cancelled) setGroup(g); });
    return () => { cancelled = true; };
  }, [isOpen, invoice?.groupId, invoice?.updatedAt]);

  // Fall back to the single document we were handed: invoices created before
  // batching have no groupId, and the group fetch fails quietly.
  const items = useMemo(() => {
    const raw = group?.items?.length ? group.items : (invoice ? [invoice] : []);
    // The group endpoint already returns items in resolved order, legacy rows
    // included. Sorting here would only second-guess it, so it is reserved for
    // the fallback path where we hold plain documents.
    return raw.every((i) => typeof i?.ordinal === "number") ? raw : sortInvoiceItems(raw);
  }, [group, invoice]);

  // Header money is the sum of what this viewer can actually see. For a manager
  // looking at part of an invoice that is a subtotal, not the invoice total —
  // which is exactly what the partial-view banner says.
  const money = useMemo(() => {
    const rates = [...new Set(
      items.map((i) => (i?.gstRate == null ? null : Number(i.gstRate))).filter((r) => r != null)
    )];
    return {
      subtotal: items.reduce((a, i) => a + (Number(i?.amount) || 0), 0),
      gst: items.reduce((a, i) => a + (Number(i?.gstAmount) || 0), 0),
      total: items.reduce((a, i) => a + linePayable(i), 0),
      // One rate for the whole invoice is the normal case; a legacy batch can
      // hold several, and then no single percentage is true.
      gstRate: rates.length === 1 ? rates[0] : null,
    };
  }, [items]);

  // The number that goes beside that money. From batching onward a submission
  // draws one number, so a group holds a single value and this is simply it. A
  // legacy submission drew a number per line — RD 38, RD 39, RD 40 were three
  // separate invoices — and keeps the range it was actually issued, which is the
  // label the list and the printed PDF already give it.
  //
  // Derived from `items`, never from `group.invoiceNo` (that is only the first
  // visible line's number): the label and `money` must describe the same set of
  // lines, or the header reads "RD 38" over a total that is 38 + 39 + 40.
  const rd = useMemo(() => {
    const nos = [...new Set(
      items.map((i) => i?.invoiceNo).filter((n) => n != null && n !== "")
    )];
    const nums = nos.map((n) => Number(n)).filter((n) => Number.isFinite(n));
    const isMulti = nos.length > 1;
    return {
      // `isMulti` drives the caveat below independently of the label, so money
      // spanning several numbers is never presented as one invoice's even if a
      // number is unparseable and no range can be built.
      label: isMulti && nums.length === nos.length
        ? `RD ${Math.min(...nums)}–${Math.max(...nums)}`
        : formatInvoiceNo(invoice),
      isMulti,
      count: nos.length,
    };
  }, [items, invoice]);

  const timeline = useMemo(() => {
    if (!invoice) return [];
    const items = [];
    (invoice.statusHistory || []).forEach((s) => items.push({
      kind: "status",
      at: s.changedAt,
      from: s.from,
      to: s.to,
      note: s.note,
      by: s.changedBy?.displayName || "Unknown",
      byRole: s.changedBy?.role || "",
    }));
    (invoice.editHistory || []).forEach((e) => items.push({
      kind: "edit",
      at: e.changedAt,
      changes: e.changes || [],
      by: e.changedBy?.displayName || "Unknown",
      byRole: e.changedBy?.role || "",
    }));
    (invoice.communicationLog || []).forEach((c) => items.push({
      kind: "comm",
      at: c.sentAt,
      channel: c.channel,
      recipients: c.recipients,
      subject: c.subject,
      label: c.label,
      message: c.message,
      attachmentsIncluded: c.attachmentsIncluded,
      success: c.success,
      by: c.sentBy?.displayName || "Unknown",
      byRole: c.sentBy?.role || "",
    }));
    return items.sort((a, b) => new Date(b.at) - new Date(a.at));
  }, [invoice]);

  if (!isOpen || !invoice) return null;

  const currency = group?.currency || invoice.currency;
  // The vendor's own paperwork number is one value for the whole invoice. Older
  // records carry it per line, hence the fallback to the document in hand.
  const referenceNo = (group?.referenceNo || invoice.referenceNo || "").trim();
  const showReference = referenceNo && referenceNo !== formatInvoiceNo(invoice);

  const fmt = (d, withTime = true) =>
    d ? moment(d).format(withTime ? "MMM D, YYYY · h:mm A" : "MMM D, YYYY") : "—";
  const fmtDate = (d) => (d ? moment.utc(d).format("MMM D, YYYY") : "—");
  const fmtSubmitted = (inv) => {
    const d = inv?.createdAt || inv?.invoiceDate;
    return d ? moment(d).format("MMM D, YYYY") : "—";
  };

  return (
    <>
      <div className={`iv-drawer-backdrop${isOpen ? " is-open" : ""}`} onClick={onClose} />
      <aside className={`iv-drawer${isOpen ? " is-open" : ""}`}>
        <header className="iv-drawer__header">
          <span className="iv-drawer__no">{rd.label}</span>
          {showReference && (
            <span className="iv-drawer__ref">Vendor Ref {referenceNo}</span>
          )}
          <StatusPill status={invoice.status} />

          <h3 className="iv-drawer__title" title={invoice.title || "Untitled invoice"}>
            {invoice.title || "Untitled invoice"}
          </h3>

          <span className="iv-drawer__vendor">
            <FeatherIcon icon="briefcase" size={13} />
            {invoice.vendorName || "—"}
          </span>

          {invoice.billingGroup?.name && (
            <span className="iv-drawer__fact" title="Property group">
              <FeatherIcon icon="layers" size={13} />
              <i>Group</i>{invoice.billingGroup.name}
            </span>
          )}
          {invoice.generatedFrom && (
            <span className="iv-drawer__fact" title="The invoice PDF was created from the vendor's approved template">
              <FeatherIcon icon="zap" size={13} />
              <i>Generated from</i>
              {invoice.generatedFrom.templateName || "template"} v{invoice.generatedFrom.version}
            </span>
          )}

          <span className="iv-drawer__fact">
            <FeatherIcon icon="calendar" size={13} />
            <i>Submitted</i>{fmtSubmitted(invoice)}
          </span>
          {(() => {
            const overdue =
              invoice.dueDate &&
              invoice.status !== "Paid" &&
              invoice.status !== "Declined" &&
              moment.utc(invoice.dueDate).startOf("day").valueOf() <= moment().startOf("day").valueOf();
            return (
              <span className="iv-drawer__fact" style={overdue ? { color: "#B91C1C" } : undefined}>
                <FeatherIcon icon={overdue ? "alert-triangle" : "clock"} size={13} />
                <i>Due</i>{fmtDate(invoice.dueDate)}{overdue ? " · Overdue" : ""}
              </span>
            );
          })()}
          {invoice.paidAt && (
            <span className="iv-drawer__fact iv-drawer__fact--paid">
              <FeatherIcon icon="check-circle" size={13} />
              <i>Paid</i>{fmtDate(invoice.paidAt)}
            </span>
          )}

          {invoice.enteredAcctAt ? (
            <span
              className="iv-drawer__fact"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "#ECFDF5", color: "#065F46", border: "1px solid #A7F3D0",
                borderRadius: 20, padding: "3px 12px", fontSize: "0.74rem", fontWeight: 700,
              }}
            >
              <FeatherIcon icon="book-open" size={13} color="#059669" />
              Entered in accounting · {moment(invoice.enteredAcctAt).format("MMM D, YYYY")}
              {invoice.enteredAcctBy?.displayName ? ` · ${invoice.enteredAcctBy.displayName}` : ""}
            </span>
          ) : (
            <span
              className="iv-drawer__fact"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                color: "#64748B", fontSize: "0.74rem", fontWeight: 600,
              }}
            >
              <FeatherIcon icon="book" size={13} color="#94A3B8" />
              Not entered in accounting
            </span>
          )}
          {canSetEntered && (
            <button
              type="button"
              className="iv-btn"
              style={
                invoice.enteredAcctAt
                  ? { background: "#fff", border: "1px solid #E2E8F0", color: "#64748B", marginTop: 6, alignSelf: "flex-start" }
                  : { background: "#059669", borderColor: "#059669", color: "#fff", marginTop: 6, alignSelf: "flex-start" }
              }
              disabled={enteringAcct}
              onClick={toggleEnteredAcct}
            >
              {enteringAcct ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <FeatherIcon icon={invoice.enteredAcctAt ? "x" : "book-open"} size={14} />{" "}
                  {invoice.enteredAcctAt ? "Clear" : "Mark entered in accounting"}
                </>
              )}
            </button>
          )}

          {/* Summed across the items this viewer can see, not read off the one
              document the drawer was opened on — so the chip above names every
              line counted here, a legacy range included. */}
          <span className="iv-drawer__amount">
            {formatCurrency(money.total, currency)}
          </span>
          {rd.isMulti && (
            <span className="iv-drawer__fact">
              <FeatherIcon icon="layers" size={13} />
              <i>Submission total</i>{rd.count} separately numbered invoices
            </span>
          )}
          <span className="iv-drawer__fact">
            <FeatherIcon icon="percent" size={13} />
            <i>Subtotal</i>{formatCurrency(money.subtotal, currency)} · GST{money.gstRate != null ? ` ${money.gstRate}%` : ""} ({formatCurrency(money.gst, currency)})
          </span>
          {invoice.status === "Declined" && invoice.declineReason && (
            <span className="iv-drawer__fact" style={{ color: "#B91C1C" }}>
              <FeatherIcon icon="x-circle" size={13} />
              <i>Declined</i>{invoice.declineReason}
            </span>
          )}

          <div className="iv-drawer__head-actions">
            <button type="button" className="iv-icon-btn" title="Print invoice" onClick={() => printInvoice(invoice)}>
              <FeatherIcon icon="printer" size={14} />
            </button>
            <button type="button" className="iv-icon-btn" title="Download PDF" onClick={handleDownload} disabled={downloading}>
              {downloading ? <Spinner size="sm" /> : <FeatherIcon icon="download" size={14} />}
            </button>
            <button type="button" className="iv-icon-btn" title="Refresh" onClick={refresh} disabled={refreshing}>
              {refreshing ? <Spinner size="sm" /> : <FeatherIcon icon="refresh-cw" size={14} />}
            </button>
            {onPreview && (
              <button type="button" className="iv-icon-btn" title="Preview invoice" onClick={onPreview}>
                <FeatherIcon icon="eye" size={14} />
              </button>
            )}
            {canEdit && (
              <button type="button" className="iv-icon-btn" title="Edit" onClick={onEdit}>
                <FeatherIcon icon="edit-2" size={14} />
              </button>
            )}
            {canEdit && (
              <button type="button" className="iv-icon-btn iv-icon-btn--green" title="Forward" onClick={onForward}>
                <FeatherIcon icon="send" size={14} />
              </button>
            )}
            <button type="button" className="iv-icon-btn" title="Close" onClick={onClose}>
              <FeatherIcon icon="x" size={14} />
            </button>
          </div>
        </header>

        {((canApprove && invoice.status === "Pending") ||
          (canMarkPaid && invoice.status === "Approved")) && (
          <div style={{
            display: "flex", gap: 8, alignItems: "center",
            padding: "12px 20px", borderBottom: "1px solid #EEF2F7", background: "#F8FAFC",
          }}>
            {canApprove && invoice.status === "Pending" && (
              <>
                <button
                  type="button" className="iv-btn iv-btn--primary"
                  style={{ background: "#059669", borderColor: "#059669" }}
                  disabled={busy} onClick={() => onApprove?.(invoice)}
                >
                  <FeatherIcon icon="check" size={14} /> Approve
                </button>
                <button
                  type="button" className="iv-btn"
                  style={{ background: "#FEF2F2", border: "1px solid #FECDD3", color: "#DC2626" }}
                  disabled={busy} onClick={() => onDecline?.(invoice)}
                >
                  <FeatherIcon icon="x" size={14} /> Decline
                </button>
              </>
            )}
            {canMarkPaid && invoice.status === "Approved" && (
              <button
                type="button" className="iv-btn iv-btn--primary"
                style={{ background: "#059669", borderColor: "#059669" }}
                disabled={busy} onClick={() => onMarkPaid?.(invoice)}
              >
                <FeatherIcon icon="dollar-sign" size={14} /> Mark as Paid
              </button>
            )}
            {busy && <Spinner size="sm" style={{ color: "#4F46E5" }} />}
          </div>
        )}

        <div className="iv-drawer__body">
          {/* The only thing on screen explaining why a manager's total is
              smaller than a lead's for the same invoice. */}
          {group?.isPartial && (
            <div style={{
              background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 9,
              padding: "10px 13px", fontSize: "0.78rem", color: "#92400E", margin: "0 0 12px",
            }}>
              <b>Showing {group.visibleItemCount} of {group.itemCount} items.</b>{" "}
              This invoice also covers properties outside your assignment. The total
              shown is your portion, not the invoice total.
            </div>
          )}

          {/* Comments (read-only — new comments are added from the table) */}
          <Section icon="message-square" title="Comments" badge={invoice.commentsHistory?.length || 0} defaultOpen={false}>
            {(invoice.commentsHistory || []).length === 0 ? (
              <div style={{ color: "#94A3B8", fontSize: "0.82rem", textAlign: "center", padding: 12 }}>
                No comments yet.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {invoice.commentsHistory.slice().reverse().map((c, idx) => (
                  <div key={c._id || idx} className="iv-comment">
                    <div className="iv-comment__avatar">
                      {(c.author?.displayName || "?").charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="iv-comment__meta">
                        <strong>{c.author?.displayName || "Unknown"}</strong>
                        <span>{c.author?.role || ""}</span>
                        <span>· {fmt(c.createdAt)}</span>
                      </div>
                      <div className="iv-comment__body">{c.comment}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Unified timeline (status + communications) */}
          <Section icon="activity" title="Activity & Communication Log" badge={timeline.length} defaultOpen={false}>
            {timeline.length === 0 ? (
              <div style={{ color: "#94A3B8", fontSize: "0.82rem", textAlign: "center", padding: 12 }}>
                No activity yet.
              </div>
            ) : (
              <ol className="iv-timeline">
                {timeline.map((t, idx) => (
                  <li key={idx} className="iv-timeline__item">
                    <div className={`iv-timeline__dot iv-timeline__dot--${t.kind === "status" ? "status" : t.kind === "edit" ? "edit" : t.channel}`}>
                      <FeatherIcon
                        icon={
                          t.kind === "status"
                            ? "flag"
                            : t.kind === "edit"
                              ? "edit-2"
                              : t.channel === "email"
                                ? "mail"
                                : "message-circle"
                        }
                        size={11}
                        color="#fff"
                      />
                    </div>
                    <div className="iv-timeline__card">
                      <div className="iv-timeline__head">
                        <strong>
                          {t.kind === "status"
                            ? `Status changed to "${t.to}"`
                            : t.kind === "edit"
                              ? `Invoice edited · ${t.changes.length} ${t.changes.length === 1 ? "field" : "fields"}`
                              : t.label
                                ? t.label
                                : t.channel === "email"
                                  ? "Email sent"
                                  : "SMS sent"}
                        </strong>
                        <span>{fmt(t.at)}</span>
                      </div>
                      {t.kind === "edit" && (
                        <div className="iv-timeline__body">
                          {t.changes.map((c, i) => (
                            <div key={i} style={{ marginBottom: 3 }}>
                              <span style={{ fontWeight: 600, color: "#0F172A" }}>{c.label}:</span>{" "}
                              <span style={{ color: "#94A3B8", textDecoration: "line-through" }}>{c.from}</span>
                              {" → "}
                              <span style={{ fontWeight: 600, color: "#0F172A" }}>{c.to}</span>
                            </div>
                          ))}
                          <span style={{ color: "#64748B" }}>By {t.by} ({t.byRole})</span>
                        </div>
                      )}
                      {t.kind === "status" && (
                        <div className="iv-timeline__body">
                          {t.from ? <>From <em>{t.from}</em> → <em>{t.to}</em><br /></> : null}
                          {t.note ? <span style={{ color: "#475569" }}>Note: {t.note}<br /></span> : null}
                          <span style={{ color: "#64748B" }}>By {t.by} ({t.byRole})</span>
                        </div>
                      )}
                      {t.kind === "comm" && (
                        <div className="iv-timeline__body">
                          {t.subject && <div><span>Subject:</span> {t.subject}</div>}
                          {t.message && (
                            <div style={{ color: "#475569", whiteSpace: "pre-wrap", margin: "4px 0" }}>
                              "{t.message}"
                            </div>
                          )}
                          <div style={{ marginTop: 4 }}>
                            <span>Recipients:</span>{" "}
                            {(t.recipients || []).map((r, i) => (
                              <span
                                key={i}
                                style={{
                                  display: "inline-block",
                                  marginRight: 4,
                                  padding: "1px 8px",
                                  borderRadius: 10,
                                  fontSize: "0.7rem",
                                  background: r.success ? "#ECFDF5" : "#FEF2F2",
                                  color: r.success ? "#065F46" : "#991B1B",
                                  border: r.success ? "1px solid #A7F3D0" : "1px solid #FECDD3",
                                }}
                                title={r.error || ""}
                              >
                                {r.target} {r.success ? "✓" : "✕"}
                              </span>
                            ))}
                          </div>
                          {t.attachmentsIncluded > 0 && (
                            <div style={{ color: "#64748B", marginTop: 2 }}>
                              📎 {t.attachmentsIncluded} attachment(s) included
                            </div>
                          )}
                          <div style={{ color: "#64748B", marginTop: 4 }}>By {t.by} ({t.byRole})</div>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Section>
        </div>
      </aside>
    </>
  );
};

export { InvoiceDetailDrawer };
